import React, { useEffect, useState } from "react";
import {
  Visibility,
  VisibilityOff,
  AccountBalanceWalletOutlined,
} from "@mui/icons-material";
import {
  getAgentDetailsRequest,
  getAgentDetailsReset,
} from "../../../store/actions";
import { connect, useDispatch } from "react-redux";
import { Card, Typography, Box, CircularProgress } from "@mui/joy";
import { STATUS } from "../../../constant";
import { toast } from "react-toastify";
import { PageTitle, StatusBadge } from "../../../components";
import { useLocation } from "react-router-dom";

const AgentCard = ({
  loginResult,
  getAgentDetailsResult,
  getAgentDetailsStatus,
  getAgentDetailsErrorMessage,
}) => {
  const { state } = useLocation();
  const { id } = state;
  const dispatch = useDispatch();
  const [showDetails, setShowDetails] = useState(false);

  const isLoading = getAgentDetailsStatus === STATUS.LOADING;
  const hidden = !getAgentDetailsResult || !showDetails;

  const handleToggle = () => {
    if (isLoading) return;
    if (!getAgentDetailsResult) {
      dispatch(getAgentDetailsRequest(loginResult.token, { id }));
    }
    setShowDetails(!showDetails);
  };

  const handleReset = () => {
    dispatch(getAgentDetailsReset());
    setShowDetails(false);
  };

  useEffect(() => {
    if (getAgentDetailsStatus === STATUS.ERROR) {
      toast.error(getAgentDetailsErrorMessage, {
        toastId: "getAgentDetailsError",
      });
    }
  }, [getAgentDetailsErrorMessage, getAgentDetailsStatus]);

  const Row = ({ label, value }: { label: string; value: string }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
        {label}
      </Typography>
      <Typography sx={{ color: "white", fontSize: 14, fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <PageTitle title={"Agent Bank Details"} />
      <Card
        sx={{
          width: 340,
          backgroundColor: "background.appcolor",
          borderRadius: 4,
          mb: 3,
          p: 2.5,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <AccountBalanceWalletOutlined sx={{ fontSize: 36, color: "white" }} />

          {/* Eye toggle with loader */}
          <Box
            onClick={handleToggle}
            sx={{
              width: 38,
              height: 38,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.1)",
              cursor: isLoading ? "not-allowed" : "pointer",
              transition: "background 0.2s",
              "&:hover": {
                bgcolor: isLoading
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(255,255,255,0.2)",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress
                size="sm"
                sx={{
                  "--CircularProgress-trackColor": "rgba(255,255,255,0.2)",
                  "--CircularProgress-progressColor": "white",
                }}
              />
            ) : hidden ? (
              <Visibility sx={{ fontSize: 20, color: "white" }} />
            ) : (
              <VisibilityOff sx={{ fontSize: 20, color: "white" }} />
            )}
          </Box>
        </Box>

        {/* Account Name */}
        <Typography
          sx={{ color: "white", fontSize: 18, fontWeight: 700, mb: 0.5 }}
        >
          {isLoading
            ? "Loading..."
            : hidden
              ? "**** **** ****"
              : getAgentDetailsResult?.agentAccountName}
        </Typography>

        {/* Status badge */}
        {!hidden && getAgentDetailsResult?.status && (
          <StatusBadge color="success" sx={{ alignSelf: "flex-start", mb: 2 }}>
            {getAgentDetailsResult.status}
          </StatusBadge>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 1 }}>
          <Row
            label="Account No"
            value={
              isLoading
                ? "Loading..."
                : hidden
                  ? "••••••••"
                  : getAgentDetailsResult?.agentPrimaryAccount
            }
          />
          <Row
            label="Currency"
            value={
              isLoading
                ? "..."
                : hidden
                  ? "•••"
                  : getAgentDetailsResult?.currency
            }
          />

          {/* Balance */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              mt: 0.5,
            }}
          >
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
              Balance
            </Typography>
            <Typography sx={{ color: "white", fontSize: 24, fontWeight: 800 }}>
              {isLoading
                ? "Loading..."
                : hidden
                  ? "••••••"
                  : `${getAgentDetailsResult?.currency} ${Number(getAgentDetailsResult?.accountBalance).toLocaleString()}`}
            </Typography>
          </Box>
        </Box>

        {/* Reset link */}
        {getAgentDetailsResult && (
          <Box
            onClick={handleReset}
            sx={{
              mt: 2,
              alignSelf: "flex-end",
              fontSize: 11,
              color: "#fbbf24",
              bgcolor: "rgba(251, 191, 36, 0.15)",
              px: 1.2,
              py: 0.5,
              borderRadius: 8,
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(251, 191, 36, 0.25)" },
            }}
          >
            reset
          </Box>
        )}
      </Card>
    </Box>
  );
};

const mapStateToProps = ({ auth, agent }) => {
  const { otpResult: loginResult } = auth;
  const {
    getAgentDetailsResult,
    getAgentDetailsStatus,
    getAgentDetailsErrorMessage,
  } = agent;
  return {
    loginResult,
    getAgentDetailsResult,
    getAgentDetailsStatus,
    getAgentDetailsErrorMessage,
  };
};

export default connect(mapStateToProps, {})(AgentCard);
